import type { AiChatConversation, AiChatMessage } from "@/api/ai-chat";
import type { ConversationKind, Scope } from "@/ai-chat/sseClient";

import {
  HCIC_DEMO_PROD_ENV_ID,
  getCurrentEnvId,
  isHcicDemoEnv,
} from "../hcic-demo";
import { PREVIEW_ENV_ID } from "../runtime";
import { HCIC_SEED_THREADS } from "./hcic-responses";
import {
  blocksToStoredContent,
  getResponseForPrompt,
  kindHidesFirstUserMessage,
} from "./responses";

// Conversations the user creates in preview mode live here for the lifetime
// of the page load. Reload starts fresh — same contract as the synthetic
// graph, which rebuilds from seed each load (see graph/index.ts).
export type StoredMessage = {
  id: string;
  ordinal: number;
  role: "user" | "assistant";
  // Anthropic-shaped content blocks: text → { type: "text", text }, structured
  // emit_<kind> tool calls → { type: "tool_use", name, input }. Matches what
  // extractAssistantBlocks / extractUserText in useConversationStream.ts read.
  content: { blocks: Array<Record<string, unknown>> };
  createdAt: string;
  // Mirrors the production CROChatMessage.VisibleToUser flag. The first user
  // prompt of an app-generated conversation (weekly_summary, funnel_dig_deeper)
  // is persisted with visibleToUser=false; the simulator/REST surface filters
  // those out before the chat UI sees them. Treated as `true` when undefined.
  visibleToUser?: boolean;
};

// Which demo narrative a *seeded* conversation belongs to. Undefined on
// user-created conversations, which always show regardless of env. listed
// conversations are filtered to the current env's profile so the HCIC booth-demo
// env shows only Kylie's seeded threads and every other env shows Meridian's.
export type DemoProfile = "meridian" | "hcic";

export type StoredConversation = {
  id: string;
  title: string;
  kind: ConversationKind;
  scope: Scope;
  status: "active" | "deleted";
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  messages: StoredMessage[];
  demoProfile?: DemoProfile;
};

export const PREVIEW_UID = "preview-uid-00000000";

const conversations = new Map<string, StoredConversation>();
let seeded = false;

function isoDaysAgo(days: number): string {
  const ms = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

// Seeded conversations so the sidebar isn't empty on first visit and each of
// the major narrative threads shows at least one example.
function seedHistorical(): void {
  // temporary threads to support demo for house call laguna beach 2026
  const attributionAt = isoDaysAgo(1);
  const assistAt = isoDaysAgo(2);
  const seoAt = isoDaysAgo(3);

  // permanent demo threads
  const sessionReplayAt = isoDaysAgo(4);
  const dropoffAt = isoDaysAgo(5);
  const weeklyAt = isoDaysAgo(6);

  const seoPrompt =
    "What are we doing well with SEO that would help with our paid search strategy?";
  const seoBlocks = getResponseForPrompt({
    prompt: seoPrompt,
    conversationKind: "standard",
  });
  conversations.set("preview-conv-seo", {
    id: "preview-conv-seo",
    title: "What are we doing well with SEO that would help with our paid ...",
    kind: "standard",
    scope: "current",
    status: "active",
    createdAt: seoAt,
    updatedAt: seoAt,
    lastMessageAt: seoAt,
    messages: [
      {
        id: "preview-msg-seo-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: seoPrompt }],
        },
        createdAt: seoAt,
      },
      {
        id: "preview-msg-seo-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(seoBlocks) },
        createdAt: seoAt,
      },
    ],
  });

  const assistPrompt =
    "Which platforms are assisting with conversions that aren't showing up in last-touch attribution?";
  const assistBlocks = getResponseForPrompt({
    prompt: assistPrompt,
    conversationKind: "standard",
  });
  conversations.set("preview-conv-assist", {
    id: "preview-conv-assist",
    title: "Which platforms are assisting with conversions that aren't sho...",
    kind: "standard",
    scope: "current",
    status: "active",
    createdAt: assistAt,
    updatedAt: assistAt,
    lastMessageAt: assistAt,
    messages: [
      {
        id: "preview-msg-assist-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: assistPrompt }],
        },
        createdAt: assistAt,
      },
      {
        id: "preview-msg-assist-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(assistBlocks) },
        createdAt: assistAt,
      },
    ],
  });

  const attributionPrompt =
    "Which channels are generating the most conversions when looking at multi-touch attribution?";
  const attributionBlocks = getResponseForPrompt({
    prompt: attributionPrompt,
    conversationKind: "standard",
  });
  conversations.set("preview-conv-attribution", {
    id: "preview-conv-attribution",
    title: "Which channels are generating the most conversions when looki...",
    kind: "standard",
    scope: "current",
    status: "active",
    createdAt: attributionAt,
    updatedAt: attributionAt,
    lastMessageAt: attributionAt,
    messages: [
      {
        id: "preview-msg-attribution-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: attributionPrompt }],
        },
        createdAt: attributionAt,
      },
      {
        id: "preview-msg-attribution-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(attributionBlocks) },
        createdAt: attributionAt,
      },
    ],
  });

  // weekly_summary dispatches on conversationKind, not prompt text, so the
  // synthetic first prompt is hidden (visibleToUser=false) just like the
  // production app-generated weekly summary.
  const weeklyPrompt = "Generate the weekly analytics summary for my account.";
  const weeklyBlocks = getResponseForPrompt({
    prompt: weeklyPrompt,
    conversationKind: "weekly_summary",
  });
  conversations.set("preview-conv-weekly", {
    id: "preview-conv-weekly",
    title: "Weekly Summary: example",
    kind: "weekly_summary",
    scope: "current",
    status: "active",
    createdAt: weeklyAt,
    updatedAt: weeklyAt,
    lastMessageAt: weeklyAt,
    messages: [
      {
        id: "preview-msg-weekly-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: weeklyPrompt }],
        },
        createdAt: weeklyAt,
        visibleToUser: !kindHidesFirstUserMessage("weekly_summary"),
      },
      {
        id: "preview-msg-weekly-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(weeklyBlocks) },
        createdAt: weeklyAt,
      },
    ],
  });

  const dropoffPrompt =
    "Why is there a large drop-off in the appointment booking funnel?";
  const dropoffBlocks = getResponseForPrompt({
    prompt: dropoffPrompt,
    conversationKind: "standard",
  });
  conversations.set("preview-conv-dropoff", {
    id: "preview-conv-dropoff",
    title: "Why is there a large drop-off in the appointment booking funne...",
    kind: "standard",
    scope: "current",
    status: "active",
    createdAt: dropoffAt,
    updatedAt: dropoffAt,
    lastMessageAt: dropoffAt,
    messages: [
      {
        id: "preview-msg-dropoff-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: dropoffPrompt }],
        },
        createdAt: dropoffAt,
      },
      {
        id: "preview-msg-dropoff-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(dropoffBlocks) },
        createdAt: dropoffAt,
      },
    ],
  });

  const sessionReplayPrompt =
    "Show me recordings of people who dropped off at insurance verification.";
  const sessionReplayBlocks = getResponseForPrompt({
    prompt: sessionReplayPrompt,
    conversationKind: "standard",
  });
  conversations.set("preview-conv-session-replay", {
    id: "preview-conv-session-replay",
    title:
      "Show me recordings of people who dropped off at insurance verifica...",
    kind: "standard",
    scope: "current",
    status: "active",
    createdAt: sessionReplayAt,
    updatedAt: sessionReplayAt,
    lastMessageAt: sessionReplayAt,
    messages: [
      {
        id: "preview-msg-session-replay-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: sessionReplayPrompt }],
        },
        createdAt: sessionReplayAt,
      },
      {
        id: "preview-msg-session-replay-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(sessionReplayBlocks) },
        createdAt: sessionReplayAt,
      },
    ],
  });

  // Everything seeded above is the Meridian narrative. Tag it so the HCIC env
  // filters it out of the sidebar.
  for (const conv of conversations.values()) {
    conv.demoProfile = "meridian";
  }

  seedHcicHistorical();
}

function seedHcicHistorical(): void {
  HCIC_SEED_THREADS.forEach(({ id, prompt }, i) => {
    const at = isoDaysAgo(i + 1);
    const blocks = getResponseForPrompt({
      prompt,
      conversationKind: "standard",
      envId: HCIC_DEMO_PROD_ENV_ID,
    });
    conversations.set(id, {
      id,
      title: prompt.length > 60 ? `${prompt.slice(0, 60)}...` : prompt,
      kind: "standard",
      scope: "current",
      status: "active",
      createdAt: at,
      updatedAt: at,
      lastMessageAt: at,
      demoProfile: "hcic",
      messages: [
        {
          id: `${id}-user`,
          ordinal: 0,
          role: "user",
          content: { blocks: [{ type: "text", text: prompt }] },
          createdAt: at,
        },
        {
          id: `${id}-asst`,
          ordinal: 1,
          role: "assistant",
          content: { blocks: blocksToStoredContent(blocks) },
          createdAt: at,
        },
      ],
    });
  });
}

function ensureSeeded(): void {
  if (seeded) return;
  seedHistorical();
  seeded = true;
}

export function listConversations(): AiChatConversation[] {
  ensureSeeded();
  const currentProfile: DemoProfile = isHcicDemoEnv(getCurrentEnvId())
    ? "hcic"
    : "meridian";
  return (
    Array.from(conversations.values())
      .filter((c) => c.status !== "deleted")
      // Seeded threads only show for their own demo profile; user-created
      // conversations (no profile) always show.
      .filter((c) => !c.demoProfile || c.demoProfile === currentProfile)
      .map(toApiConversation)
  );
}

export function getConversation(id: string): {
  conversation: AiChatConversation;
  messages: AiChatMessage[];
} | null {
  ensureSeeded();
  const conv = conversations.get(id);
  if (!conv || conv.status === "deleted") return null;
  // Mirror the production REST surface, which runs FilterMessagesForUser on
  // the way out so callers never see hidden prompts.
  return {
    conversation: toApiConversation(conv),
    messages: conv.messages
      .filter((m) => m.visibleToUser !== false)
      .map((m) => toApiMessage(conv.id, m)),
  };
}

export function upsertConversation(input: {
  id: string;
  title: string;
  status: string;
  kind?: ConversationKind;
  scope?: Scope;
}): void {
  ensureSeeded();
  const existing = conversations.get(input.id);
  const now = new Date().toISOString();
  if (existing) {
    existing.title = input.title;
    existing.status = input.status === "deleted" ? "deleted" : "active";
    existing.updatedAt = now;
    if (input.kind) existing.kind = input.kind;
    // Scope is immutable once the conversation exists (mirrors the backend),
    // so a later save never changes it.
    return;
  }
  conversations.set(input.id, {
    id: input.id,
    title: input.title,
    kind: input.kind ?? "standard",
    scope: input.scope ?? "current",
    status: input.status === "deleted" ? "deleted" : "active",
    createdAt: now,
    updatedAt: now,
    lastMessageAt: null,
    messages: [],
  });
}

export function appendMessage(
  conversationId: string,
  msg: Omit<StoredMessage, "createdAt">,
): StoredMessage | null {
  ensureSeeded();
  const conv = conversations.get(conversationId);
  if (!conv) return null;
  const stored: StoredMessage = { ...msg, createdAt: new Date().toISOString() };
  conv.messages.push(stored);
  conv.lastMessageAt = stored.createdAt;
  conv.updatedAt = stored.createdAt;
  return stored;
}

export function nextOrdinal(conversationId: string): number {
  const conv = conversations.get(conversationId);
  if (!conv || conv.messages.length === 0) return 0;
  return conv.messages[conv.messages.length - 1].ordinal + 1;
}

export function getStoredConversation(id: string): StoredConversation | null {
  ensureSeeded();
  return conversations.get(id) ?? null;
}

export function __resetPreviewAiChatStore(): void {
  conversations.clear();
  seeded = false;
}

function toApiConversation(conv: StoredConversation): AiChatConversation {
  return {
    id: conv.id,
    env_id: PREVIEW_ENV_ID,
    uid: PREVIEW_UID,
    title: conv.title,
    kind: conv.kind,
    status: conv.status,
    processing_status: "idle",
    processing_started_at: null,
    last_message_at: conv.lastMessageAt,
    created_at: conv.createdAt,
    updated_at: conv.updatedAt,
  };
}

function toApiMessage(
  conversationId: string,
  msg: StoredMessage,
): AiChatMessage {
  return {
    id: msg.id,
    env_id: PREVIEW_ENV_ID,
    uid: PREVIEW_UID,
    conversation_id: conversationId,
    parent_message_id: null,
    role: msg.role,
    content: msg.content,
    model_config: {},
    usage: {},
    status: "final",
    visible_to_user: msg.visibleToUser !== false,
    created_at: msg.createdAt,
  };
}
